'use client'

import {Form, Formik} from 'formik'
import {FormField, FormWarning} from 'layout/form/form'
import React, {useEffect, useState} from 'react'

import AnimateHeight from 'react-animate-height'
import {DPJobsBlock} from 'types/DPTypes'
import {Icon} from 'assets/icon'
import {JOBALERT_URL} from 'util/urls'
import {Theme} from 'layout/theme'
import {algoliaFilterMap} from './jobsfilters'
import css from './jobalerts.module.scss'
import {fromModule} from 'util/styler/Styler'
import {useInstantSearch} from 'react-instantsearch-hooks'
import {useSearchParams} from 'next/navigation'
import {useTranslation} from 'util/i18'

const styles = fromModule(css)

export const JobAlerts: React.FC<DPJobsBlock> = (params) => {
	const [open, setOpen] = useState(false)
	const searchParams = useSearchParams()
	const alerts = searchParams.get('alerts')

	useEffect(() => {
		if (!alerts) return
		if (alerts === 'open') setOpen(true)
	}, [alerts])

	return (
		<div className={styles.jobalerts()} id="content">
			<JobAlertsBar
				open={open}
				toggleOpen={() => {
					setOpen((prev) => !prev)
				}}
			/>
			<AnimateHeight height={open ? 'auto' : 0} animateOpacity={false}>
				<JobAlertsForm {...params} />
			</AnimateHeight>
		</div>
	)
}

const JobAlertsBar: React.FC<{open: boolean; toggleOpen: () => void}> = ({
	open,
	toggleOpen
}) => {
	const t = useTranslation()
	return (
		<div className={styles.bar()} onClick={toggleOpen}>
			<span>
				<Icon icon="Bell" className={styles.bar.bell()} />
			</span>
			<span className={styles.bar.label()}>{t.jobs.alerts.button}</span>
			<span>
				<Icon icon="ChevronDown" className={styles.bar.chevron.is({open})()} />
			</span>
		</div>
	)
}

const JobAlertsForm: React.FC<DPJobsBlock> = ({intro, types}) => {
	const t = useTranslation()
	const [msg, setMsg] = useState('')
	const [submitting, setSubmitting] = useState(false)
	const [submitted, setSubmitted] = useState(false)
	const {indexUiState} = useInstantSearch()
	return (
		<div className={styles.form()}>
			<Formik<{email?: string; agree_terms?: boolean}>
				initialValues={{}}
				onSubmit={(values, {setErrors, resetForm}) => {
					if (submitting) return
					if (!values.email) {
						setErrors({email: t.jobs.alerts.errors.email})
						return
					}
					if (!values.agree_terms) {
						setErrors({agree_terms: t.jobs.alerts.errors.terms})
						return
					}
					if (!indexUiState.refinementList) {
						setMsg(t.jobs.alerts.errors.filter)
						return
					}
					setSubmitting(true)

					const categories = {}
					for (const attribute in indexUiState.refinementList) {
						const typesKey = Object.keys(algoliaFilterMap).find(
							(afkey) => algoliaFilterMap[afkey] === attribute
						)
						if (!typesKey) continue
						categories[typesKey] = []
						for (const refinement of indexUiState.refinementList[attribute]) {
							const typesObj = types[typesKey].find((t) => t.tid === refinement)
							if (!typesObj) continue
							categories[typesKey].push({
								title: typesObj.name,
								id: typesObj.tid
							})
						}
					}

					const body = {
						...values,
						categories,
						jobtype:
							(indexUiState.configure as any).filters.indexOf(
								'show_career:true'
							) >= 0
								? 'career'
								: 'academic',
						query: indexUiState.query || null
					}

					fetch(JOBALERT_URL(), {
						method: 'POST',
						headers: {'Content-Type': 'application/json'},
						body: JSON.stringify(body)
					})
						.then((response) => {
							if (response.ok) {
								resetForm()
								setSubmitted(true)
								setMsg(t.jobs.alerts.thank_you)
							} else {
								return response.json().then((errors) => setErrors(errors))
							}
						})
						.catch(() => {
							setMsg('Something went wrong, please try again later.')
						})
						.finally(() => {
							setSubmitting(false)
						})
				}}
			>
				<Form>
					<p className={styles.form.intro()}>
						{intro ? intro : t.jobs.alerts.intro}
					</p>
					<div className={styles.form.email()}>
						<FormField
							name="email"
							type="email"
							required
							mod="dark"
							label={t.jobs.alerts.email}
						/>
					</div>
					<div className={styles.form.checkbox()}>
						<FormField
							name="agree_terms"
							type="checkbox"
							required
							label={t.jobs.alerts.agree_terms}
						/>
					</div>
					<div>
						<Theme.Button as="button" type="submit" mod={['rounded']}>
							<Theme.ButtonText>{t.jobs.alerts.activate}</Theme.ButtonText>
							<Theme.ButtonIcon />
						</Theme.Button>
					</div>
					<FormWarning warning={msg} />
				</Form>
			</Formik>
		</div>
	)
}
